import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { MicrosoftExcelLogo } from "@phosphor-icons/react";
import api, { money } from "../lib/api";
import { DataTable } from "./DataTable";
import { TableRow, TableCell } from "./ui/table";
import { Button } from "./ui/button";

export const useProjectLedger = (projectId) =>
  useQuery({
    queryKey: ["project-ledger", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/ledger-entries`)).data,
    enabled: !!projectId,
    staleTime: 0,
    refetchOnMount: "always",
  });

const columns = [
  { key: "entry_no", header: "Entry No.", numeric: true, cell: (r) => <span className="font-mono text-xs">{r.entry_no}</span> },
  { key: "posting_date", header: "Posting Date", filter: "text" },
  { key: "source", header: "Source", filter: "select", options: [{ value: "PO", label: "Purchase Order" }, { value: "STR", label: "Stock Transfer" }] },
  { key: "type", header: "Type", filter: "select", options: ["Item", "G/L Account"] },
  { key: "document_no", header: "Document No.", filter: "text", cell: (r) => <span className="font-mono text-xs" data-testid="ledger-doc-no">{r.document_no}</span> },
  { key: "external_doc_no", header: "PO No.", filter: "text", cell: (r) => <span className="font-mono text-xs">{r.external_doc_no || "—"}</span> },
  { key: "item_no", header: "No.", filter: "text", cell: (r) => (r.item_id ? <Link to={`/inventory/item/${r.item_id}`} className="text-primary hover:underline font-mono text-xs">{r.item_no || "Item"}</Link> : "—") },
  { key: "description", header: "Description", filter: "text" },
  { key: "quantity", header: "Quantity", numeric: true, align: "right", cell: (r) => <span className="tabular">{r.quantity}</span> },
  { key: "unit_of_measure", header: "UoM" },
  { key: "unit_cost", header: "Unit Cost", numeric: true, align: "right", cell: (r) => <span className="tabular">{money(r.unit_cost)}</span> },
  { key: "total_cost", header: "Total Cost", numeric: true, align: "right", cell: (r) => <span className={`tabular font-semibold ${r.total_cost < 0 ? "text-destructive" : ""}`} data-testid="ledger-total-cost">{money(r.total_cost)}</span> },
];

const XL_COLS = [
  ["Entry No.", "entry_no"], ["Posting Date", "posting_date"], ["Source", "source"], ["Type", "type"],
  ["Document No.", "document_no"], ["PO No.", "external_doc_no"], ["No.", "item_no"], ["Description", "description"],
  ["Vendor", "vendor_name"], ["Quantity", "quantity"], ["UoM", "unit_of_measure"], ["Unit Cost", "unit_cost"], ["Total Cost", "total_cost"],
];

function exportLedger(rows, code) {
  const aoa = [XL_COLS.map((c) => c[0]), ...rows.map((r) => XL_COLS.map(([, k]) => r[k] ?? ""))];
  const total = rows.reduce((s, r) => s + Number(r.total_cost || 0), 0);
  aoa.push(XL_COLS.map((c, i) => (i === 0 ? "Total" : i === XL_COLS.length - 1 ? Math.round(total * 100) / 100 : "")));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Project Ledger");
  XLSX.writeFile(wb, `${code || "project"}-ledger-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export function ProjectLedgerTable({ projectId, projectCode }) {
  const { data, isLoading } = useProjectLedger(projectId);
  const entries = data?.entries || [];
  const exportBtn = (view) => (
    <Button size="sm" variant="outline" disabled={view.length === 0} onClick={() => exportLedger(view, projectCode)} className="h-8 rounded-sm text-xs gap-1.5" data-testid="project-ledger-export">
      <MicrosoftExcelLogo size={15} /> Export to Excel
    </Button>
  );
  const footer = (view) => view.length > 0 && (
    <TableRow className="bg-secondary/40 font-bold">
      <TableCell colSpan={columns.length - 1} className="text-right text-xs uppercase tracking-wide">Total Cost</TableCell>
      <TableCell className="text-right tabular" data-testid="project-ledger-grand-total">{money(view.reduce((s, r) => s + Number(r.total_cost || 0), 0))}</TableCell>
    </TableRow>
  );
  return (
    <div className="overflow-x-auto">
      <DataTable
        data={entries} columns={columns} rowKey={(r) => r.id} rowTestid="project-ledger-row"
        testid="project-ledger-table" loading={isLoading} footer={footer}
        empty="No ledger entries yet. Receive a PO with lines booked to this project, or post a Stock Transfer."
        searchPlaceholder="Search entries…" toolbarExtra={exportBtn}
      />
    </div>
  );
}
